/*
 * Input: single-line text. Joins a surrounding <Field>. Optional start/end adornments (icons, units).
 * `mono` for keys, hostnames and codes (LTR, IBM Plex Mono). Email/URL/tel inputs are LTR automatically.
 *   <Input placeholder="server1.example.sa" mono start={<Globe />} />
 */
import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { controlClasses, controlHeight } from './control'
import { useFieldProps } from './Field'

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  start?: ReactNode
  end?: ReactNode
  mono?: boolean
  /** LTR text (passwords, codes) without the mono font; the box keeps the page direction */
  ltr?: boolean
  inputClassName?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { start, end, mono, ltr, className, inputClassName, ...props },
  ref,
) {
  const field = useFieldProps(props)
  return (
    <div className={cn('relative flex items-center', className)}>
      {start ? (
        <span className="pointer-events-none absolute start-3 flex items-center text-subtle [&_svg]:size-[18px]">
          {start}
        </span>
      ) : null}
      <input
        ref={ref}
        {...props}
        {...field}
        data-ltr={mono || ltr ? '' : undefined}
        className={cn(
          controlClasses,
          controlHeight,
          'px-3',
          start && 'ps-10',
          end && 'pe-10',
          mono && 'font-mono text-13 tracking-tight',
          inputClassName,
        )}
      />
      {end ? (
        <span className="absolute end-1.5 flex items-center text-subtle [&_svg]:size-[18px]">{end}</span>
      ) : null}
    </div>
  )
})
